/**
 * Where each drafter got to in each lesson, kept in this browser.
 *
 * Per user: the key carries the signed-in user's id, so two people on
 * one machine do not share progress. A lesson counts as watched from 90%
 * of its length (the closing credits and "next lesson" slide are not
 * worth waiting for). Storage can be unavailable (private mode, blocked
 * site data); then nothing is remembered and nothing breaks.
 */

export interface LessonProgress {
  /** Seconds into the lesson when last seen. */
  position: number;
  /** The lesson's length as the player reported it. */
  duration: number;
  watched: boolean;
}

export type ProgressMap = Record<string, LessonProgress>;

export const WATCHED_AT = 0.9;
/** Do not offer to resume a lesson only just started or nearly done. */
const RESUME_FROM = 5;

function storageKey(userId: string | number | null | undefined): string {
  return `courses_progress:${userId ?? 'anonymous'}`;
}

export function readProgress(userId?: string | number | null): ProgressMap {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed as ProgressMap : {};
  } catch {
    return {};
  }
}

export function writeProgress(userId: string | number | null | undefined, map: ProgressMap): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(map));
  } catch {
    // Private mode or storage disabled — progress is just not kept.
  }
}

/** ``map`` with this lesson's position recorded; once watched, a lesson
 * stays watched even if the drafter goes back to the start. */
export function recordPosition(
  map: ProgressMap, id: string, position: number, duration: number,
): ProgressMap {
  if (!Number.isFinite(position) || !Number.isFinite(duration) || duration <= 0) return map;
  const before = map[id];
  const watched = !!before?.watched || position / duration >= WATCHED_AT;
  // Near the end, resume from the start next time.
  const kept = position / duration >= WATCHED_AT ? 0 : position;
  if (before && Math.abs(before.position - kept) < 1 && before.watched === watched) return map;
  return { ...map, [id]: { position: kept, duration, watched } };
}

export function markWatched(map: ProgressMap, id: string, duration: number): ProgressMap {
  if (map[id]?.watched && map[id]?.position === 0) return map;
  return { ...map, [id]: { position: 0, duration: duration || map[id]?.duration || 0, watched: true } };
}

/** Where to resume, or null to start from the beginning. */
export function resumeAt(progress: LessonProgress | undefined, duration: number): number | null {
  if (!progress) return null;
  const length = duration > 0 ? duration : progress.duration;
  if (progress.position < RESUME_FROM || (length > 0 && progress.position / length >= WATCHED_AT)) {
    return null;
  }
  return progress.position;
}

/** 0–1 for a progress bar. */
export function fractionSeen(progress: LessonProgress | undefined, seconds?: number | null): number {
  if (!progress) return 0;
  if (progress.watched) return 1;
  const length = progress.duration || seconds || 0;
  return length > 0 ? Math.min(1, progress.position / length) : 0;
}
